// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  FedExConfirmation,
  FedExConfirmationDetails,
  ProposedFedExPickupTime,
} from "./FedExTypes";
import React, { useEffect } from "react";
import {
  addDays,
  endOfDay,
  format,
  parse as parseDate,
  startOfDay,
} from "date-fns";
import { createStyles, makeStyles } from "@material-ui/core";

import { Address } from "./FedExCommon";
import NextStepLink from "components/TestRun/NextStepLink";
import Paragraph from "components/ui/Paragraph";
import { TestRunParams } from "components/TestRun/TestRunConstants";
import TestRunStepLink from "components/TestRun/TestRunStepLink";
import Title from "components/ui/Title";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useParams } from "react-router-dom";

const useStyles = makeStyles(theme =>
  createStyles({
    detail: {
      fontWeight: "bold",
      wordWrap: "break-word",
    },
    noData: {
      color: "gray",
      fontStyle: "italic",
    },
  })
);

const PACKAGE_LOCATION_LABELS = {
  NONE: "No location provided",
  FRONT: "Front",
  SIDE: "Side",
  REAR: "Rear",
};

interface FedExConfirmationProps {
  confirmation: FedExConfirmation;
  details?: FedExConfirmationDetails;
}
const FedExConfirmationPage = ({
  confirmation: { pickupWindow, confirmation },
  details,
}: FedExConfirmationProps) => {
  const { step } = useParams<TestRunParams>();
  useEffect(() => {
    setStepReady(step, true);
  }, [step]);

  const classes = useStyles();

  const startTime = toHumanReadableStartTime(pickupWindow);

  return (
    <>
      <Title>Pick up confirmed</Title>
      <Paragraph>
        Your FedEx pickup is confirmed. Before <strong>{startTime}</strong>,
        place the package in the pickup location, out of direct sunlight.
      </Paragraph>
      <Paragraph>
        Pickup window:
        <div className={classes.detail}>{pickupWindow.humanTime}</div>
      </Paragraph>
      {details && (
        <>
          <Paragraph>
            Pickup location:
            {details.packageLocation !== "NONE" && (
              <div className={classes.detail}>
                {PACKAGE_LOCATION_LABELS[details.packageLocation]}
              </div>
            )}
            <Address className={classes.detail} address={details.address} />
            <div className={classes.detail}></div>
          </Paragraph>
          <Paragraph>
            Additional instructions:
            {details.remarks ? (
              <div className={classes.detail}>{details.remarks}</div>
            ) : (
              <div className={classes.noData}>No instructions provided</div>
            )}
          </Paragraph>
        </>
      )}
      <Paragraph>
        Confirmation number:
        <div className={classes.detail}>{confirmation}</div>
      </Paragraph>
      <Paragraph>
        If you need to change the pickup window,{" "}
        <TestRunStepLink nav="callToChange">contact FedEx</TestRunStepLink>.
      </Paragraph>
      <NextStepLink />
    </>
  );
};

export default FedExConfirmationPage;

function toHumanReadableStartTime(
  pickupWindow: ProposedFedExPickupTime
): string {
  const startTime = parseDate(
    pickupWindow.date + " " + pickupWindow.startTime,
    "yyyy-MM-dd HH:mm:ss",
    new Date()
  );
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const tomorrowEnd = endOfDay(addDays(today, 1));

  if (startTime > todayStart && startTime <= todayEnd) {
    return format(startTime, "h:mma 'today'"); // "2:30pm today"
  }
  if (startTime > todayEnd && startTime <= tomorrowEnd) {
    return format(startTime, "h:mma 'tomorrow'"); // "2:30pm tomorrow"
  }
  return format(startTime, "h:mma 'on' MMMM do"); // "2:30pm on July 17th"
}
